/*
One directory listing, packed.

An entry is an index into parallel arrays. The directory path and the local
directory handle live on the FsDir. Files have no property storage until a
property is set. The screen does not hold these indexes. It uses FsRow.
*/

import { isString, len, throwIf } from "./util";

export const kInvalidEntry = -1;

export type FsEntry = number;
export type FsProp = [string, any];

export type FsRow = {
  id: string;
  name: string;
  isDir: boolean;
  size: number;
  lastMod: number;
  props: FsProp[];
};

export const kUpRow: FsRow = {
  id: "..",
  name: "..",
  isDir: true,
  size: -1,
  lastMod: 0,
  props: [],
};

export class FsDir {
  path: string;
  handle: FileSystemDirectoryHandle | null;

  names: string[] = [];
  // empty string means "use the name"
  ids: string[] = [];
  sizes: number[] = [];
  modTimes: number[] = [];
  dirBits: boolean[] = [];
  // null until the entry has at least one property
  propLists: (FsProp[] | null)[] = [];

  fileCount = 0;

  constructor(path: string, handle: FileSystemDirectoryHandle | null = null) {
    this.path = path;
    this.handle = handle;
  }

  count(): number {
    return len(this.names);
  }

  private check(e: FsEntry): void {
    throwIf(e < 0 || e >= len(this.names), `bad entry ${e}`);
  }

  private add(
    name: string,
    id: string,
    size: number,
    modTime: number,
    isDir: boolean
  ): FsEntry {
    let e = len(this.names);
    this.names.push(name);
    this.ids.push(id);
    this.sizes.push(size);
    this.modTimes.push(modTime);
    this.dirBits.push(isDir);
    this.propLists.push(null);
    if (!isDir) {
      this.fileCount++;
    }
    return e;
  }

  /**
   * Append one wire row.
   * [id, name, size, lastMod, entries, ...key, value]
   */
  appendWire(meta: any[]): FsEntry {
    throwIf(!Array.isArray(meta), "meta not an array");
    throwIf(len(meta) < 5, "invalid meta");
    let id = isString(meta[0]) ? (meta[0] as string) : "";
    let name = meta[1];
    throwIf(!isString(name), "meta name must be a string");
    let size = meta[2] as number;
    let lastMod = meta[3];
    if (isString(lastMod)) {
      let timeMs = 0;
      if (lastMod != "") {
        timeMs = new Date(lastMod as string).getTime();
      }
      lastMod = timeMs;
    }
    let entries = meta[4];
    let isDir = size === -1 || Array.isArray(entries);
    if (isDir) {
      throwIf(size !== -1, "dir must have size -1");
      throwIf(entries != null && !Array.isArray(entries), "dir entries must be null or []");
    } else {
      throwIf(Array.isArray(entries), "file entries must be null");
    }
    let e = this.add(name as string, id, isDir ? -1 : size, (lastMod as number) || 0, isDir);
    let n = len(meta);
    for (let i = 5; i < n; i += 2) {
      throwIf(i + 1 >= n, `key ${meta[i]} without value`);
      this.entrySetMeta(e, meta[i], meta[i + 1]);
    }
    return e;
  }

  static fromWire(dirPath: string, metas: any[][]): FsDir {
    let dir = new FsDir(dirPath);
    for (let meta of metas) {
      dir.appendWire(meta);
    }
    return dir;
  }

  addFile(name: string, size: number, modTime: number, id: string = ""): FsEntry {
    return this.add(name, id, size, modTime, false);
  }

  addDir(name: string, id: string = ""): FsEntry {
    return this.add(name, id, -1, 0, true);
  }

  entryName(e: FsEntry): string {
    this.check(e);
    return this.names[e];
  }

  entryId(e: FsEntry): string {
    this.check(e);
    let id = this.ids[e];
    if (id === "") {
      return this.names[e];
    }
    return id;
  }

  entrySize(e: FsEntry): number {
    this.check(e);
    return this.sizes[e];
  }

  setEntrySize(e: FsEntry, size: number): void {
    this.check(e);
    this.sizes[e] = size;
  }

  entryModTime(e: FsEntry): number {
    this.check(e);
    return this.modTimes[e];
  }

  setEntryModTime(e: FsEntry, t: number): void {
    this.check(e);
    this.modTimes[e] = t;
  }

  entryIsDir(e: FsEntry): boolean {
    this.check(e);
    return this.dirBits[e];
  }

  entrySetMeta(e: FsEntry, key: string, val: any): void {
    this.check(e);
    let list = this.propLists[e];
    if (!list) {
      list = [];
      this.propLists[e] = list;
    }
    for (let prop of list) {
      if (prop[0] === key) {
        prop[1] = val;
        return;
      }
    }
    list.push([key, val]);
  }

  entryMeta(e: FsEntry, key: string): any {
    this.check(e);
    let list = this.propLists[e];
    if (!list) {
      return null;
    }
    for (let prop of list) {
      if (prop[0] === key) {
        return prop[1];
      }
    }
    return null;
  }

  entryProps(e: FsEntry): FsProp[] {
    this.check(e);
    let list = this.propLists[e];
    if (!list) {
      return [];
    }
    return list.slice();
  }

  row(e: FsEntry): FsRow {
    return {
      id: this.entryId(e),
      name: this.entryName(e),
      isDir: this.entryIsDir(e),
      size: this.entrySize(e),
      lastMod: this.entryModTime(e),
      props: this.entryProps(e),
    };
  }

  rows(): FsRow[] {
    let n = this.count();
    let res: FsRow[] = [];
    for (let i = 0; i < n; i++) {
      res.push(this.row(i));
    }
    return res;
  }

  /** Sum of direct file sizes. */
  totalSize(): number {
    let n = 0;
    let count = this.count();
    for (let i = 0; i < count; i++) {
      if (!this.dirBits[i]) {
        n += this.sizes[i];
      }
    }
    return n;
  }

  findChild(name: string): FsEntry {
    let n = this.count();
    for (let i = 0; i < n; i++) {
      if (this.names[i] === name) {
        return i;
      }
    }
    return kInvalidEntry;
  }

  removeChildByName(name: string): void {
    let i = this.findChild(name);
    if (i === kInvalidEntry) {
      throw `Didn't find entry ${name} in ${this.path}`;
    }
    if (!this.dirBits[i]) {
      this.fileCount--;
    }
    this.names.splice(i, 1);
    this.ids.splice(i, 1);
    this.sizes.splice(i, 1);
    this.modTimes.splice(i, 1);
    this.dirBits.splice(i, 1);
    this.propLists.splice(i, 1);
  }
}
